import { captureCareSourceForProcessing } from './careSourceProcessingClient';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';
import {
  buildQuickCheckInCaptureSource,
  buildQuickCheckInCareSourceMetadata,
} from '../../utils/quickCheckInCaptureFirstAdapter';
import { rememberCareSourceActivity } from './careSourceSessionActivity';
import { normalizeCareSourceType } from './sourceTypes';

const SOURCE_NOTES_COLLECTION = 'careSources';

const toTrimmedString = (value) => String(value || '').trim();

const resolveUserId = (user = {}) => (
  user?.uid || user?.id || ''
);

const attachDraftToCaptureSource = (captureSource = {}, sourceNoteId = '') => ({
  ...captureSource,
  sourceNoteId,
  sourceNotePath: `${SOURCE_NOTES_COLLECTION}/${sourceNoteId}`,
  sourceNoteCollection: SOURCE_NOTES_COLLECTION,
  sourceNoteType: 'care_source',
});

export const buildQuickLogCareSourceDraftPayload = ({
  child = null,
  user = null,
  text = '',
  sourceText = text,
  normalizedText = text,
  selectedDate = null,
  selectedTime = '',
  importantMoment = false,
  routingDecision = {},
  routingPreview = {},
  transcriptNormalization = {},
  structureDecision = {},
  quickNoteCategoryDecision = null,
  sourceType = 'quick_check_in',
  sourceMode = 'quick_log_capture_first',
  inputMode = 'capture_first',
  metadata = {},
  imageAttachments = [],
  audioAttachments = [],
  timestamp = serverTimestamp(),
} = {}) => {
  const rawText = String(sourceText || text || '');
  const userId = resolveUserId(user);
  const captureSource = buildQuickCheckInCaptureSource({
    text,
    sourceText: rawText,
    normalizedText,
    sourceType,
    sourceMode,
    inputMode,
    child,
    selectedDate,
    selectedTime,
    importantMoment,
    routingDecision,
    routingPreview,
    transcriptNormalization,
    structureDecision,
    quickNoteCategoryDecision,
    metadata,
  });
  const cleanAttachments = Array.isArray(imageAttachments) ? imageAttachments.filter(Boolean) : [];
  const cleanAudioAttachments = Array.isArray(audioAttachments) ? audioAttachments.filter(Boolean) : [];
  const baseSourceMetadata = buildQuickCheckInCareSourceMetadata(captureSource);
  const sourceMetadata = (cleanAttachments.length > 0 || cleanAudioAttachments.length > 0)
    ? {
      ...baseSourceMetadata,
      ...(cleanAttachments.length > 0 && { imageAttachments: cleanAttachments }),
      ...(cleanAudioAttachments.length > 0 && { audioAttachments: cleanAudioAttachments }),
      mediaUrls: [...cleanAttachments, ...cleanAudioAttachments].map((a) => a.url).filter(Boolean),
    }
    : baseSourceMetadata;

  return {
    captureSource,
    payload: {
      childId: child?.id || null,
      childName: child?.name || null,
      rawText,
      sourceText: rawText,
      originalText: rawText,
      normalizedText: toTrimmedString(normalizedText),
      sourceType: normalizeCareSourceType(sourceType),
      sourceKind: sourceMetadata.sourceKind,
      sourceContext: sourceMetadata.sourceContext,
      documentType: sourceMetadata.documentType,
      textExtractionStatus: sourceMetadata.textExtractionStatus,
      processingStatus: sourceMetadata.processingStatus,
      reviewState: sourceMetadata.reviewState,
      capturedAt: timestamp,
      capturedBy: userId,
      createdBy: userId,
      createdAt: timestamp,
      updatedAt: timestamp,
      status: 'captured',
      extractionSummary: {
        recordCount: 0,
        needsReviewCount: 0,
        categoriesFound: [],
      },
      sourceMetadata,
      ...(cleanAudioAttachments.length > 0 && { audioAttachments: cleanAudioAttachments }),
      ...(sourceMetadata.mediaUrls?.length > 0 && { mediaUrls: sourceMetadata.mediaUrls }),
      metadata: {
        source: {
          ...sourceMetadata,
          sourceKind: sourceMetadata.sourceKind,
          sourceContext: sourceMetadata.sourceContext,
          documentType: sourceMetadata.documentType,
        },
        captureFirst: {
          enabled: true,
          status: 'captured',
          sourceType: normalizeCareSourceType(sourceType),
          savedBeforeExtraction: true,
          organizeAsync: true,
        },
      },
    },
  };
};

export const saveQuickLogCareSourceDraft = async ({
  child = null,
  user = null,
  text = '',
  clientRequestId = '',
  deps = {},
  ...options
} = {}) => {
  const rawText = String(text || '');
  if (!child?.id || !rawText.trim()) {
    return null;
  }

  const resolvedDb = deps.db || db;
  const collectionFn = deps.collection || collection;
  const addDocFn = deps.addDoc || addDoc;
  const timestamp = deps.serverTimestamp ? deps.serverTimestamp() : serverTimestamp();
  const { payload, captureSource } = buildQuickLogCareSourceDraftPayload({
    child,
    user,
    text: rawText,
    timestamp,
    ...options,
  });

  const capture = deps.captureCareSourceForProcessing || captureCareSourceForProcessing;
  const docRef = clientRequestId
    ? await capture({
      childId: child.id, rawText: payload.rawText, sourceType: payload.sourceType,
      clientRequestId, sourceMetadata: payload.sourceMetadata,
    }).then((result) => {
      if (!result?.ok || !result.sourceNoteId) throw new Error('The capture was not saved.');
      return { id: result.sourceNoteId };
    })
    : await addDocFn(collectionFn(resolvedDb, SOURCE_NOTES_COLLECTION), payload);
  const sourceNoteId = docRef?.id || '';
  const captureSourceWithDraft = attachDraftToCaptureSource(captureSource, sourceNoteId);

  rememberCareSourceActivity(sourceNoteId);

  return {
    ok: Boolean(sourceNoteId),
    sourceNoteId,
    sourceNotePath: captureSourceWithDraft.sourceNotePath,
    sourceNoteCollection: SOURCE_NOTES_COLLECTION,
    sourceNoteType: 'care_source',
    status: 'captured',
    organizing: true,
    captureSource: captureSourceWithDraft,
  };
};
